import { useEffect, useRef, useState } from 'react';
import { TOUR_END_MS } from './tour-script';
import type { TourStop } from './tour-script';

/*
 * THE FLIGHT, RUNNING.
 *
 * One timer per beat rather than a frame loop, because nothing here is animated by this hook: the
 * camera tween belongs to the engine, which eases between the points it is handed, and the lines
 * are typography that appears and stays. A 60 Hz loop would wake the main thread seventeen hundred
 * times to change something six times.
 *
 * The callbacks are held in a ref so that a parent which rebuilds them every render — which is
 * every parent, since they close over the engine handle — cannot restart the flight halfway and
 * send the camera back out to the whole field.
 */

/**
 * Run the flight while `active`, and report the beat in force.
 *
 * The beat IS the output: the narration renders from it and the camera is flown to it (see
 * `MapCanvas`'s `fly`), so there is nothing imperative to hand back — only `done`, which is the one
 * thing the flight knows and its caller cannot work out from a beat.
 *
 * Returns null when it is not running, so a caller can render the narration unconditionally and
 * get nothing when there is nothing being said.
 */
export function useTour(active: boolean, stops: readonly TourStop[], done: () => void): TourStop | null {
  const [stop, setStop] = useState<TourStop | null>(null);
  const held = useRef(done);
  held.current = done;

  /* `stops` is derived from the places and rebuilt per render by any caller that maps over them;
     depending on its identity would restart the flight on every render. */
  const stopsRef = useRef(stops);
  stopsRef.current = stops;

  useEffect(() => {
    if (!active) { setStop(null); return; }
    const current = stopsRef.current;
    const timers = current.map(beat => setTimeout(() => { setStop(beat); }, beat.at));
    timers.push(setTimeout(() => held.current(), TOUR_END_MS));
    return () => { for (const timer of timers) clearTimeout(timer); };
  }, [active]);

  return stop;
}
